(function () {
  'use strict';

  class GommaDotPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.cache = new Map();
      this.abortController = null;
      this.timer = null;
      this.requestSequence = 0;
      this.suppressFocusOpen = false;
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('focus', () => {
        if (this.suppressFocusOpen) return;
        if (this.results.innerHTML.trim()) this.open();
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.requestSequence += 1;
      const sequence = this.requestSequence;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.removeAttribute('loading');
      this.input.removeAttribute('aria-busy');
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close(true);
        return;
      }
      this.timer = window.setTimeout(() => this.fetchResults(term, sequence), 220);
    }

    async fetchResults(term, sequence) {
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) {
        this.removeAttribute('loading');
        this.input.removeAttribute('aria-busy');
        if (sequence === this.requestSequence && this.input.value.trim().toLocaleLowerCase() === key) this.render(this.cache.get(key));
        return;
      }

      const controller = new AbortController();
      this.abortController = controller;
      this.setAttribute('loading', '');
      this.input.setAttribute('aria-busy', 'true');

      try {
        const route = window.gommaDOT.routes.predictiveSearch;
        const query = window.gommaDOT.exactSearchQuery?.(term) || term;
        const strict = query !== term;
        const url = new URL(route, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', strict ? 'product' : 'product,query,collection');
        url.searchParams.set('resources[limit]', strict ? '10' : '8');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode');
        url.searchParams.set('section_id', 'predictive-search');
        const fetchSection = async () => {
          const response = await fetch(url.toString(), { signal: controller.signal });
          if (!response.ok) throw new Error(String(response.status));
          const text = await response.text();
          return new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        };
        let section = await fetchSection();
        if (strict && section && !section.querySelector('[data-search-product]')) {
          const words = term.split(/\s+/);
          url.searchParams.set('q', words.slice(1).join(' '));
          section = await fetchSection();
          const normalize = (value) => value.toLocaleLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').match(/[\p{L}\p{N}]+/gu) || [];
          const needles = normalize(term);
          section?.querySelectorAll('[data-search-product]').forEach((item) => {
            const haystack = normalize(item.dataset.searchProduct);
            if (!needles.every((word, index) => haystack.some((candidate) => index === needles.length - 1 ? candidate.startsWith(word) : candidate === word))) item.remove();
          });
        }
        if (strict && section) {
          const hasProducts = Boolean(section.querySelector('[data-search-product]'));
          section.querySelector('.predictive-results__products')?.toggleAttribute('hidden', !hasProducts);
          const empty = section.querySelector('[data-search-empty]');
          if (empty) {
            empty.hidden = hasProducts;
            empty.textContent = this.dataset.emptyText.replace('__terms__', term);
          }
          const all = section.querySelector('[data-search-view-all]');
          if (all) {
            const destination = new URL(window.gommaDOT.routes.search, window.location.origin);
            destination.searchParams.set('q', query);
            destination.searchParams.set('type', 'product');
            destination.searchParams.set('options[unavailable_products]', 'last');
            destination.searchParams.set('options[prefix]', 'none');
            all.href = destination.toString();
            all.querySelector('span').textContent = this.dataset.viewAllText.replace('__terms__', term);
          }
        }
        const markup = section ? section.innerHTML : '';
        this.cache.set(key, markup);
        if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
        if (sequence === this.requestSequence && this.input.value.trim().toLocaleLowerCase() === key) this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close(true);
      } finally {
        if (this.abortController === controller) {
          this.abortController = null;
          this.removeAttribute('loading');
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      if (markup.trim()) this.open(); else this.close();
    }

    open() {
      this.input.setAttribute('aria-expanded', 'true');
      this.results.hidden = false;
    }

    close(clear) {
      this.input.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      if (clear) this.results.innerHTML = '';
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.suppressFocusOpen = true;
        this.input.focus();
        window.requestAnimationFrame(() => { this.suppressFocusOpen = false; });
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.input.getAttribute('aria-expanded') !== 'true') return;
      const links = Array.from(this.results.querySelectorAll('a,button')).filter((item) => !item.disabled);
      if (!links.length) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
      links[next].focus();
    }
  }

  if (!customElements.get('g-predictive-search')) customElements.define('g-predictive-search', GommaDotPredictiveSearch);
})();
